// permissionService.ts
import { fetchUtils } from 'react-admin';
import { parseRoleData } from './permissionParser.ts';
import PermissionCacheStore from './cacheService.ts';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';

function getHeaders() {
  const headers = new Headers({ Accept: 'application/json' });
  // Try session first, then token
  const authToken = localStorage.getItem('session') || localStorage.getItem('token');
  if (authToken) headers.set('Authorization', `Bearer ${authToken}`);
  return headers;
}

// Fetch permissions for the current userId with cache:
// 1. 先检查缓存
// 2. GET /users/:userId to find role id
// 3. GET /roles/:roleId to obtain roleMenus/roleActions
export async function fetchUserPermissions(userId: number | string) {
  try {
    const userIdStr = String(userId);
    
    // 1. 检查缓存
    const cached = PermissionCacheStore.getCache(userIdStr, 'permissions');
    if (cached) {
      return cached;
    }

    // 2. 获取用户信息并找到角色ID
    const userResp = await fetchUtils.fetchJson(
      `${apiUrl}/permissionUsers/${userId}`,
      { headers: getHeaders() }
    );
    const user = userResp.json;

    const roleId = user?.roleId || user?.role?.id || (Array.isArray(user?.roles) && user.roles[0]?.id);
    if (!roleId) {
      if (user?.roleMenus) {
        const result = parseRoleData(user);
        PermissionCacheStore.setCache(userIdStr, 'permissions', result);
        return result;
      }
      return { menus: [], actionIds: [], actionCodes: [] };
    }

    // 3. 获取角色权限
    const roleResp = await fetchUtils.fetchJson(
      `${apiUrl}/permissionroles/${roleId}`,
      { headers: getHeaders() }
    );

    const role = roleResp.json;
    const result = parseRoleData(role);
    
    // 4. 缓存结果
    PermissionCacheStore.setCache(userIdStr, 'permissions', result);
    
    return result;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('fetchUserPermissions failed', err);
    return { menus: [], actionIds: [], actionCodes: [] };
  }
}

// Fetch permissions directly by role id with cache
export async function fetchPermissionsByRoleId(roleId: number | string) {
  try {
    const cacheKey = `role_${roleId}`;
    
    // 1. 检查缓存
    const cached = PermissionCacheStore.getCache('global', cacheKey);
    if (cached) {
      return cached;
    }

    // 2. 获取角色权限
    const roleResp = await fetchUtils.fetchJson(
      `${apiUrl}/permissionroles/${roleId}`,
      { headers: getHeaders() }
    );
    const role = roleResp.json;
    const result = parseRoleData(role);
    
    // 3. 缓存结果
    PermissionCacheStore.setCache('global', cacheKey, result);
    
    return result;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('fetchPermissionsByRoleId failed', err);
    return { menus: [], actionIds: [], actionCodes: [] };
  }

}

// Fetch a single menu by id
export async function fetchMenuById(menuId: number | string) {
  try {
    const resp = await fetchUtils.fetchJson(`${apiUrl}/permissionMenus/${menuId}`, { headers: getHeaders() });
    return resp.json;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('fetchMenuById failed for', menuId, err);
    return null;
  }
}

// Given an array of flat menus returned by role permissions, ensure all ancestor menus are included
export async function fetchMenusWithParents(initialMenus: any[]) {
  const map = new Map<number, any>();
  initialMenus.forEach(m => { if (m && m.id) map.set(m.id, m); });

  const toCheck = new Set<number>();
  map.forEach(m => {
    if (m.parentId != null && !map.has(m.parentId)) toCheck.add(m.parentId);
  });

  // 并行获取所有缺失的菜单（层级扩展）
  while (toCheck.size > 0) {
    const batchIds = Array.from(toCheck);
    toCheck.clear();

    // 并行获取所有缺失的菜单
    const parentPromises = batchIds.map(id => fetchMenuById(id));
    const parents = await Promise.all(parentPromises);

    parents.forEach(parent => {
      if (parent && parent.id && !map.has(parent.id)) {
        map.set(parent.id, parent);
        // 将祖父菜单加入待检查列表
        if (parent.parentId != null && !map.has(parent.parentId)) {
          toCheck.add(parent.parentId);
        }
      }
    });
  }

  return Array.from(map.values());
}
